import { Input, RadioButton, Typography } from '@weareopenr/component-library';
import { Control, FieldValues, UseFormRegister, useFormState, useWatch } from 'react-hook-form';
import { AdjustmentType } from 'src/types/price.types';
import styles from './PriceRuleAdjustmentOptions.module.scss';
import { RuleCalculationFields } from 'src/types/priceRules';

interface PriceRuleAdjustmentOptionsProps {
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  entity: string;
}

const PriceRuleAdjustmentOptions = ({ register, control }: PriceRuleAdjustmentOptionsProps) => {
  const adjustmentType = useWatch({ name: RuleCalculationFields.ADJUSTMENT_TYPE, control });
  const { errors } = useFormState({ control });

  return (
    <div className={styles['container']}>
      <div className={styles['options']}>
        <Typography size="large" weight="bold">
          Automated Adjustments
        </Typography>
        <RadioButton name={RuleCalculationFields.ADJUSTMENT_TYPE} value={AdjustmentType.NONE} register={register}>
          <Typography weight="semi-bold">No Calculation Adjustment</Typography>
        </RadioButton>
        <div className={styles['option']}>
          <RadioButton name={RuleCalculationFields.ADJUSTMENT_TYPE} value={AdjustmentType.FIXED} register={register}>
            <Typography weight="semi-bold">Adjust by Fixed Amount</Typography>
          </RadioButton>
          <Typography>Adjust prices by adding or subtracting a specific value to the default price.</Typography>
          <Input
            prefix="£"
            label="Fixed Amount"
            register={register(RuleCalculationFields.ADJUSTMENT_FIXED, {
              valueAsNumber: true,
              required: {
                value: adjustmentType === AdjustmentType.FIXED,
                message: 'Please enter an amount',
              },
            })}
            type="number"
            step={0.01}
            placeholder="00.00"
            disabled={adjustmentType !== AdjustmentType.FIXED}
            error={errors?.['adjustment']?.['fixed']?.message?.toString()}
          />
        </div>
        <div className={styles['option']}>
          <RadioButton
            name={RuleCalculationFields.ADJUSTMENT_TYPE}
            value={AdjustmentType.PERCENTAGE}
            register={register}
          >
            <Typography weight="semi-bold">Adjust by Percentage</Typography>
          </RadioButton>
          <Typography>Adjust prices by increasing or decreasing a percentage of the default price.</Typography>
          <Input
            label="Percentage"
            suffix="%"
            register={register(RuleCalculationFields.ADJUSTMENT_PERCENTAGE, {
              valueAsNumber: true,
              required: {
                value: adjustmentType === AdjustmentType.PERCENTAGE,
                message: 'Please enter a percentage',
              },
              validate: (value) => {
                if (adjustmentType === AdjustmentType.PERCENTAGE && Number(value) === 0) {
                  return 'Percentage cannot be zero';
                }
                return true;
              },
            })}
            type="number"
            step={0.01}
            placeholder="00.00"
            disabled={adjustmentType !== AdjustmentType.PERCENTAGE}
            error={errors?.['adjustment']?.['percentage']?.message?.toString()}
          />
        </div>
      </div>
    </div>
  );
};

export default PriceRuleAdjustmentOptions;
